/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from 'react';
import { createStyles, useMantineTheme, Stack } from '@mantine/core';
import { z } from 'zod';
import dt from '~/domains/contest/domains/managev2/domains/common/testing.const';
import type { CellComponentProps, CellDescriptor } from '../../types/common';
import { getDependenciesValues } from '../../../../../context/utils/getDependencyValues';
import { getIsDependenciesSatisfied } from '../../../../../context/utils/getIsDependenciesSatisfied';
import useAlignStyle from '../../useAlignStyle';
import { IconLock } from '@tabler/icons-react';
import { Text } from '~/domains/design-system/Text';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';
import type { TableCellDataMultiplierType, TableColumnMultiplierType } from './types';
import { MultiplierTextInput } from './MultiplierTextInput';

const useStyles = createStyles((theme) => ({
  cellWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
  },
  inputWrapper: {
    border: `1px solid ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.md,
    backgroundColor: theme.white,
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ '6px 16px 2px 16px',
    '&:focus-within': {
      borderColor: theme.colors.blue[6],
    },
    '&[data-disabled="true"]': {
      backgroundColor: theme.colors.gray[1],
      cursor: 'not-allowed',
    },
    '&[data-error="true"]': {
      borderColor: theme.colors.red[6],
    },
  },
  lock: {
    display: 'flex',
    alignItems: 'center',
  },
}));

function TableCellMultiplier({
  column,
  row,
  cell,
  value,
  allValues,
  sectionKey,
  onChange,
  allErrors,
}: CellComponentProps<TableColumnMultiplierType, TableCellDataMultiplierType>) {
  const theme = useMantineTheme();
  const [isCellDisabled, setCellDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { classes, cx } = useStyles();
  const { classes: alignClasses } = useAlignStyle();

  const numValue = typeof value === 'number' ? value : undefined;

  useEffect(() => {
    if (!cell.disabledWhen) return;
    const dependencyValues = getDependenciesValues(allValues, sectionKey, cell.disabledWhen);
    const isDependencySatisfied = getIsDependenciesSatisfied(
      row.key,
      cell.disabledWhen,
      allErrors,
      dependencyValues
    );

    setCellDisabled(isDependencySatisfied);
  }, [
    JSON.stringify(allErrors),
    JSON.stringify(allValues),
    cell.disabledWhen,
    sectionKey,
    row.key,
  ]);

  useEffect(() => {
    if (typeof cell.defaultValue === 'undefined') {
      return;
    }
    if (typeof value !== 'undefined') {
      return;
    }

    onChange(cell.defaultValue, true);
  }, [cell.defaultValue, onChange, value]);

  const handleChange = useCallback(
    (newValue: number | undefined) => {
      const min = column.validation?.min;
      const max = column.validation?.max;

      // Validate min
      if (!column.validation?.isOptional) {
        if (min !== undefined && newValue < min) {
          setErrorMessage(
            max !== undefined
              ? `Multiplier must be between ${min}x and ${max}x`
              : `Multiplier must be at least ${min}x`
          );
          return;
        }

        // Validate max
        if (max !== undefined && newValue > max) {
          setErrorMessage(
            min !== undefined
              ? `Multiplier must be between ${min}x and ${max}x`
              : `Multiplier must be at most ${max}x`
          );
          return;
        }
        setErrorMessage(null);
      }

      onChange(newValue);
    },
    [column.validation?.min, column.validation?.max, onChange]
  );

  return (
    <div
      className={cx(classes.cellWrapper, {
        [alignClasses.start]: column.params?.align === 'start',
        [alignClasses.center]: column.params?.align === 'center',
        [alignClasses.end]: column.params?.align === 'end',
      })}
    >
      {errorMessage && (
        <Text variant="body-small" c="red.6">
          {errorMessage}
        </Text>
      )}
      <Tooltip
        isRendered={!!cell.params?.tooltip}
        withinPortal
        position="top-start"
        key={`${row.key}${column.key}`}
        label={cell.params?.tooltip}
      >
        {cell.isDisabled || isCellDisabled ? (
          <div className={classes.lock}>
            <IconLock stroke={theme.colors.gray2[7]} />
          </div>
        ) : (
          <Stack
            spacing={theme.other.spacing._2}
            align="flex-start"
            className={classes.inputWrapper}
            data-disabled={cell.isDisabled || isCellDisabled}
            data-error={!!errorMessage}
          >
            <Text variant="body-small" c="gray.6">
              Multiplier
            </Text>
            <MultiplierTextInput
              defaultValue={numValue}
              onChange={handleChange}
              isDisabled={cell.isDisabled || isCellDisabled}
              testId={dt.table.body.select}
            />
          </Stack>
        )}
      </Tooltip>
    </div>
  );
}

const createValidationSchema = (column: TableColumnMultiplierType) => {
  let schema = z.number();

  if (column.validation?.min !== undefined) {
    schema = schema.min(column.validation.min, {
      message: `Minimum allowed value is ${column.validation.min}`,
    });
  }

  if (column.validation?.max !== undefined) {
    schema = schema.max(column.validation.max, {
      message: `Maximum allowed value is ${column.validation.max}`,
    });
  }

  return {
    schema,
  };
};

const descriptor: CellDescriptor<TableColumnMultiplierType, TableCellDataMultiplierType> = {
  name: 'Multiplier',
  Cell: TableCellMultiplier,
  createValidationSchema,
};

export default descriptor;
